import Papa from 'papaparse';
import { buildExportFilename } from '@/utils/fileName';

interface FundAllocationRow {
  // Enriched by usePortfolioAllocations
  fund?: {
    fund_name?: string;
    display_name?: string;
    fund_strategy?: string;
    fund_type?: string;
  };
  asset_type?: string;
  allocation_percentage: number;
  allocation_value?: number | null;
  currency_code?: string | null;
}

interface PortfolioShape {
  name: string;
  portfolio_size?: number | null;
  portfolio_allocations?: FundAllocationRow[];
}

type AssetLike = {
  id: string;
  asset_name: string;
  symbol?: string | null;
  asset_type: string;
  currency: string;
  market_value: number;
  quantity?: number | null;
  created_at?: string;
  // Allow UnifiedAsset-specific fields (optional)
  account_identifier?: string | null;
};

export const exportPortfolioToCSV = (portfolio: PortfolioShape) => {
  console.log('Starting Excel export with portfolio:', portfolio);
  
  try {
    // Guard
    const size = Number(portfolio.portfolio_size ?? 0);

    // Format portfolio allocations for CSV
    const portfolioData = (portfolio.portfolio_allocations || []).map((allocation) => {
      const name = allocation.fund?.fund_name || allocation.fund?.display_name || '';
      const assetClass = (allocation as any)?.fund?.sa_asset_class || allocation.fund?.fund_type || allocation.asset_type || '';
      const pct = Number(allocation.allocation_percentage || 0);
      const currency = (allocation.currency_code || 'CAD').toUpperCase();
      const amount = typeof allocation.allocation_value === 'number'
        ? Number(allocation.allocation_value)
        : Number(((pct / 100) * size).toFixed(2));
      return {
        'Fund Name': name,
        'Asset Class': String(assetClass),
        'Allocation (%)': pct.toFixed(2),
        'Currency': currency,
        // Export numeric amount so spreadsheet math works
        'Amount': amount,
      };
    });

    // Prepare CSV content (add summary as header rows using Papa to ensure proper quoting)
    const summaryRows = [
      ['Portfolio', portfolio.name || ''],
      ['Total Size', Number(size.toFixed(2))],
      [''], // spacer
    ];
    const summaryCsv = Papa.unparse(summaryRows);
    const csvData = Papa.unparse(portfolioData);
    const csvContent = summaryCsv + "\n" + csvData;
    // Add BOM for Excel UTF-8 compatibility
    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = buildExportFilename('Portfolio', 'Export', { name: portfolio.name });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error in exportPortfolioToCSV:', error);
    throw error;
  }
};

export const exportAssetsToCSV = (assets: AssetLike[]) => {
  console.log('Starting assets export with assets:', assets);
  
  try {
    // Format assets data for CSV
    const assetsData = assets.map(asset => {
      const qty = typeof asset.quantity === 'number' ? (asset.quantity as number) : null;
      const mv = Number(asset.market_value || 0);
      const ppu = qty && qty !== 0 ? Number((mv / qty).toFixed(6)) : '';
      const identifier = (asset.symbol || '') || (asset as any).account_identifier || '';
      return {
        'Asset Name': asset.asset_name,
        'Ticker/Symbol/ID': identifier,
        'Asset Type': asset.asset_type,
        'Currency': (asset.currency || '').toUpperCase(),
        'Quantity': qty == null ? '' : Number(qty),
        'Price per Unit': ppu,
        'Market Value': Number(mv.toFixed(2)),
        'Date Added': asset.created_at ? new Date(asset.created_at).toISOString().split('T')[0] : '',
      };
    });
    
    // Calculate totals
    const totalValue = assets.reduce((sum, asset) => sum + asset.market_value, 0);
    
    // Prepare CSV content with summary (quoted via Papa to allow commas in numbers)
    const summaryRows = [
      ['Assets Export', new Date().toISOString().slice(0,10)],
      ['Total Assets', assets.length],
      ['Total Value', Number(totalValue.toFixed(2))],
      [''],
    ];
    const summaryCsv = Papa.unparse(summaryRows);
    const csvData = Papa.unparse(assetsData);
    const csvContent = summaryCsv + "\n" + csvData;

    // Trigger download (add BOM for Excel UTF-8 compatibility)
    const blob = new Blob(['\ufeff', csvContent], { type: 'text/csv;charset=utf-8;' });
    const fileName = buildExportFilename('Assets', 'Export');
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', fileName);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error in exportAssetsToCSV:', error);
    throw error;
  }
};
